/*
  File reading + per-row extraction for the ticket-import flow. Handles both
  CSV (read as text) and XLS / XLSX (read as array buffer with cellDates so
  Excel-serial dates survive). Per-row extraction normalises every value
  into JS primitives (string, number, Date->DD/MM/YYYY string).
*/
import * as XLSX from 'xlsx';
import { isValid, isValidArray, isValidString } from '../../utils/utilities';
import { MANDATORY_ROW_FIELDS, REQUIRED_HEADERS } from './constants';

export const normalizeHeader = (header) => {
    if (!isValidString(header)) {
        return '';
    }
    return header.toString().trim().toLowerCase().replace(/\s+/g, ' ');
};

export const buildHeaderMap = (headerRow) => {
    const map = {};
    if (!isValidArray(headerRow)) {
        return map;
    }
    headerRow.forEach((heading, index) => {
        const normalized = normalizeHeader(heading);
        if (normalized && !(normalized in map)) {
            map[normalized] = index;
        }
    });
    return map;
};

export const formatDateAsDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

// `xlsx` returns Excel serial numbers for date cells when `cellDates` is off
// and `Date` objects when it is on. We normalise both, plus regular numbers
// and strings, to a clean trimmed string so downstream parsing is uniform
// regardless of whether the source file was CSV, XLS or XLSX.
export const getCellValue = (row, headerMap, headerName) => {
    const idx = headerMap[normalizeHeader(headerName)];
    if (idx === undefined) {
        return '';
    }
    const value = row[idx];
    if (value === null || value === undefined) {
        return '';
    }
    if (value instanceof Date) {
        return formatDateAsDDMMYYYY(value);
    }
    if (typeof value === 'string') {
        return value.trim();
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value.toString() : value.toString();
    }
    if (typeof value === 'boolean') {
        return value ? 'TRUE' : 'FALSE';
    }
    return value.toString().trim();
};

export const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

export const inferGender = (seatNumber) => {
    if (!isValidString(seatNumber)) {
        return 'M';
    }
    const match = seatNumber.toString().match(/\(([MFmf])\)/);
    if (match) {
        return match[1].toUpperCase();
    }
    return 'M';
};

export const cleanSeatNumber = (seatNumber) => {
    if (!isValidString(seatNumber)) {
        return '';
    }
    return seatNumber.toString().replace(/\([MFmf]\)/, '').trim();
};

export const isActiveStatus = (status) => {
    if (!isValidString(status)) {
        return false;
    }
    const normalized = status.toString().trim().toLowerCase();
    return normalized === 'booked' || normalized === 'blocked';
};

// A row is treated as a cancellation when its Status column says Cancel /
// Cancelled, regardless of whether the Cancelled On column is populated.
// This is more permissive than relying on cancelledOn alone so rows like
// `Status = Cancel, Cancelled On = ""` are not silently dropped.
export const isCancellationStatus = (status) => {
    if (!isValidString(status)) {
        return false;
    }
    const normalized = status.toString().trim().toLowerCase();
    return normalized === 'cancel' || normalized === 'cancelled' || normalized === 'cancellation';
};

// Convert the active sheet to a 2D array. For CSVs we keep `raw: true` so the
// library returns the cells exactly as they appear in the file (otherwise xlsx
// auto-detects "01/06/2026" as a date and reformats it to "1/6/26"). For
// XLSX / XLS we use `raw: false` so date cells stored as Excel serial numbers
// come back as JS Date objects which `getCellValue` then formats.
export const parseExcelRows = (workbook, isCsv) => {
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: isCsv === true,
        blankrows: false,
        dateNF: 'dd/mm/yyyy',
    });
};

// Read the file using the right strategy per extension.
export const readWorkbookFromFile = (file) => new Promise((resolve, reject) => {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const isCsv = extension === 'csv';
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = (event) => {
        try {
            let workbook;
            if (isCsv) {
                const text = event.target.result;
                workbook = XLSX.read(text, { type: 'string', raw: true });
            } else {
                const data = new Uint8Array(event.target.result);
                workbook = XLSX.read(data, {
                    type: 'array',
                    cellDates: true,
                    cellNF: false,
                    cellText: false,
                });
            }
            resolve({ workbook, isCsv });
        } catch (error) {
            reject(error);
        }
    };
    if (isCsv) {
        reader.readAsText(file);
    } else {
        reader.readAsArrayBuffer(file);
    }
});

export const validateHeaders = (headerMap) => REQUIRED_HEADERS.filter(
    (header) => !(normalizeHeader(header) in headerMap),
);

export const isEmptyCell = (cell) => (
    cell === '' || cell === null || cell === undefined
);

// Returns the labels of any mandatory fields missing from a single parsed
// row. Fare is treated specially because numeric 0 also counts as missing.
export const getRowMissingMandatoryFields = (row) => {
    const missing = [];
    MANDATORY_ROW_FIELDS.forEach((field) => {
        const value = row[field.key];
        const stringy = value !== null && value !== undefined ? value.toString().trim() : '';
        if (stringy === '') {
            missing.push(field.label);
        }
    });
    if (!Number.isFinite(row.fare) || row.fare <= 0) {
        missing.push('Fare');
    }
    return missing;
};

export const extractTickets = (dataRows, headerMap) => {
    const rows = [];
    dataRows.forEach((row, index) => {
        if (!isValidArray(row) || row.every(isEmptyCell)) {
            return;
        }
        const pnr = getCellValue(row, headerMap, 'Pnr Number');
        // Skip the row if there's no PNR at all — every valid ticket row in
        // the export file carries one.
        if (!isValidString(pnr)) {
            return;
        }
        rows.push({
            rowNumber: index + 2,
            pnr: pnr.toString().trim(),
            journeyDate: getCellValue(row, headerMap, 'Journey Date'),
            route: getCellValue(row, headerMap, 'Route'),
            boardingPoint: getCellValue(row, headerMap, 'Boarding Point'),
            dropOffPoint: getCellValue(row, headerMap, 'Drop Off Point'),
            coach: getCellValue(row, headerMap, 'Coach'),
            name: getCellValue(row, headerMap, 'Name'),
            phoneNumber: getCellValue(row, headerMap, 'Phone number').toString(),
            email: getCellValue(row, headerMap, 'Email'),
            seatNumber: getCellValue(row, headerMap, 'Seat Number').toString(),
            fare: toNumber(getCellValue(row, headerMap, 'Fare')),
            netAmount: toNumber(getCellValue(row, headerMap, 'Net Amount')),
            bookedBy: getCellValue(row, headerMap, 'Booked By'),
            branch: getCellValue(row, headerMap, 'Branch'),
            bookedOn: getCellValue(row, headerMap, 'Booked On'),
            remarks: getCellValue(row, headerMap, 'Remarks'),
            discount: toNumber(getCellValue(row, headerMap, 'Discount')),
            issuedBy: getCellValue(row, headerMap, 'Issued By'),
            offerDiscount: toNumber(getCellValue(row, headerMap, 'Offer Discount')),
            cancelledBy: getCellValue(row, headerMap, 'Cancelled By'),
            documentNumber: getCellValue(row, headerMap, 'Document Number'),
            commission: toNumber(getCellValue(row, headerMap, 'Commission')),
            category: getCellValue(row, headerMap, 'Category'),
            gst: toNumber(getCellValue(row, headerMap, 'gst')),
            status: getCellValue(row, headerMap, 'Status'),
            serviceStatus: getCellValue(row, headerMap, 'Service Status'),
            cancelledOn: getCellValue(row, headerMap, 'Cancelled On'),
            issuedOn: getCellValue(row, headerMap, 'Issued On'),
            additionalFare: toNumber(getCellValue(row, headerMap, 'Additional Fare')),
            serviceNumber: getCellValue(row, headerMap, 'Service Number'),
            bookedFor: getCellValue(row, headerMap, 'Booked For'),
            drivers: getCellValue(row, headerMap, 'Drivers'),
        });
    });
    // Silence the unused-import warning while keeping the dependency
    // explicit at the top of the file.
    void isValid;
    return rows;
};
